/**
 * Public Portal Route (Story 14.3 — Task 8 / AC #8)
 *
 * URL: /portal/:token
 *
 * Sibling to __root.tsx — deliberately NOT nested under `_authenticated` or
 * `_auth`. The token IS the credential; no auth state is consulted.
 *
 * Flow:
 *   1. Fetch meta (GET /api/portal-view/:token/meta)
 *   2. If inactive → render PortalInactivePage
 *   3. If active + passwordProtected === true → render PortalPasswordGate
 *      - On successful verify, the server sets an HttpOnly cookie; we then
 *        re-fetch meta so the flow progresses to the shell
 *   4. Otherwise render PortalShell (which fetches /view with auto-refresh)
 *
 * NO DashboardLayout wrapper, NO sidebar, NO nav. This is the minimum
 * surface area a client needs to view their dashboard.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchPortalMeta } from "../lib/portalPublicApi";
import { PortalInactivePage } from "../components/portal/PortalInactivePage";
import { PortalPasswordGate } from "../components/portal/PortalPasswordGate";
import { PortalShell } from "../components/portal/PortalShell";

export const Route = createFileRoute("/portal/$token")({
  component: PortalPage,
});

const DEFAULT_FAVICON = "/favicon.svg";
const DEFAULT_THEME_COLOR = "#2563eb";

/**
 * Branded favicon + theme-color effect (Story 14.5 AC #7, AC #17; favicon
 * source upgraded in Story 15.2).
 *
 * Favicon precedence: (1) the dedicated uploaded `faviconUrl` when set;
 * (2) else the SVG logo (D44 — raster logos look bad at 16x16); (3) else the
 * ProvenLeap default. MUST capture the prior <link rel="icon"> href and
 * <meta name="theme-color"> content on mount and restore on unmount — prevents
 * DOM leaks during SPA navigation (CLAUDE.md guidance).
 */
function useBrandedFaviconAndThemeColor(
  logoUrl: string | null,
  faviconUrl: string | null,
  primaryColor: string | null,
) {
  useEffect(() => {
    const head = document.head;
    let iconLink = head.querySelector<HTMLLinkElement>("link[rel='icon']");
    let themeMeta = head.querySelector<HTMLMetaElement>("meta[name='theme-color']");

    // CR2 R2-F9: index.html normally ships with <link rel="icon"> +
    // <meta name="theme-color">, but if a deployment or custom host ever
    // strips them we still want AC #7 to hold. Create missing tags so the
    // branded favicon/theme always applies; track creation so the cleanup
    // removes our added node rather than leaving orphaned tags in the DOM.
    const createdIcon = iconLink === null;
    const createdTheme = themeMeta === null;
    if (!iconLink) {
      iconLink = document.createElement("link");
      iconLink.setAttribute("rel", "icon");
      head.appendChild(iconLink);
    }
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.setAttribute("name", "theme-color");
      head.appendChild(themeMeta);
    }

    const prevIconHref = createdIcon ? null : iconLink.getAttribute("href");
    const prevThemeContent = createdTheme ? null : themeMeta.getAttribute("content");

    // Story 15.2: prefer the dedicated favicon; fall back to the SVG-logo
    // heuristic, then the default.
    const useSvgFavicon =
      logoUrl !== null && /\.svg($|\?)/i.test(logoUrl);
    const nextIconHref =
      faviconUrl ?? (useSvgFavicon ? logoUrl : DEFAULT_FAVICON);
    const nextThemeContent = primaryColor ?? DEFAULT_THEME_COLOR;

    iconLink.setAttribute("href", nextIconHref);
    themeMeta.setAttribute("content", nextThemeContent);

    return () => {
      if (createdIcon) {
        iconLink?.remove();
      } else if (prevIconHref !== null) {
        iconLink?.setAttribute("href", prevIconHref);
      }
      if (createdTheme) {
        themeMeta?.remove();
      } else if (prevThemeContent !== null) {
        themeMeta?.setAttribute("content", prevThemeContent);
      }
    };
  }, [logoUrl, faviconUrl, primaryColor]);
}

function PortalPage() {
  const { token } = Route.useParams();
  // Monotonically-incremented counter — bumped after password verify so the
  // meta query re-runs (picking up the new `provenleap_portal_session` cookie).
  const [metaVersion, setMetaVersion] = useState(0);

  const metaQuery = useQuery({
    queryKey: ["portal-meta", token, metaVersion],
    queryFn: () => fetchPortalMeta(token),
    retry: false,
    // Meta is tiny + cheap; refetch only when the version bumps
    staleTime: Infinity,
  });

  const branding =
    metaQuery.data && metaQuery.data.status === "active"
      ? metaQuery.data.branding
      : null;
  useBrandedFaviconAndThemeColor(
    branding?.logoUrl ?? null,
    branding?.faviconUrl ?? null,
    branding?.primaryColor ?? null,
  );

  if (metaQuery.isLoading) {
    return <PortalShellLoading />;
  }

  if (metaQuery.isError || !metaQuery.data) {
    // Network error or invalid token shape → treat as inactive for privacy
    return <PortalInactivePage />;
  }

  const meta = metaQuery.data;

  if (meta.status === "inactive") {
    return <PortalInactivePage reason={meta.reason} />;
  }

  if (meta.passwordProtected) {
    return (
      <PortalPasswordGate
        token={token}
        label={meta.label}
        clientName={meta.clientName}
        logoUrl={meta.branding.logoUrl}
        companyName={meta.branding.companyName}
        poweredByProvenLeap={meta.branding.poweredByProvenLeap}
        onVerified={() => setMetaVersion((v) => v + 1)}
      />
    );
  }

  return (
    <PortalShell
      token={token}
      meta={meta}
      onPasswordRequired={() => setMetaVersion((v) => v + 1)}
    />
  );
}

function PortalShellLoading() {
  return (
    <main
      id="main-content"
      className="min-h-screen flex items-center justify-center bg-background"
      aria-label="Loading portal"
    >
      <div className="h-10 w-10 rounded-full border-[0.5px] border-[var(--hairline)] border-t-foreground animate-spin" />
    </main>
  );
}
